import type { Graphics } from 'pixi.js';
import { LIFT_PARTS } from '../theme.js';

/**
 * Each building's own materials (ADR-0026).
 *
 * The four buildings shared one cream wall, one pale shaft and one thin slab,
 * so they told themselves apart only by their furniture. The owner chose a mix
 * of three mocked-up directions, and this is it:
 *
 * - **Walls are the building's material.** Poured concrete with strip lights,
 *   striped wallpaper over a wooden dado, metro tiles with a sage band, glass
 *   with the city behind it.
 * - **The façade frames the floors.** The floor-number column and the right
 *   edge show the building's outside, and the slabs are thick and made of it:
 *   concrete, brick with stone quoins, pale stone with rivets, glass.
 * - **The shaft is dark, and the building's own.** Graphite, black lacquer
 *   with gold, iron green, night blue.
 *
 * Presentation only, and every colour here is measured against the floor hues
 * like everything else on screen (`palette`, tested in atmosphere.spec.ts):
 * the tickets stay the loudest thing in every building.
 */

/** Where a floor's wall is: from the waiting row to the right edge. */
export interface WallArea {
  readonly x: number;
  readonly top: number;
  readonly right: number;
  readonly height: number;
}

/** A strip of façade: the floor-number column, or the right-hand edge. */
export interface FacadeStrip {
  readonly x: number;
  readonly top: number;
  readonly width: number;
  readonly height: number;
  /** Which side of the building it is: quoins and such sit on the outer edge. */
  readonly side: 'left' | 'right';
}

export interface ShaftArea {
  readonly left: number;
  readonly right: number;
  readonly top: number;
  readonly height: number;
  /** Y of each floor's slab, where a mark is drawn on the shaft wall. */
  readonly slabs: readonly number[];
}

export interface Atmosphere {
  /** Every colour this building paints with, by name, for the colour test. */
  readonly palette: Readonly<Record<string, number>>;
  readonly wall: number;
  paintWall(g: Graphics, area: WallArea): void;
  /** Anything hung from the ceiling, drawn over the rooms. */
  paintCeiling?(g: Graphics, area: WallArea): void;
  paintFacade(g: Graphics, strip: FacadeStrip): void;
  readonly slab: number;
  /** How thick the slab is drawn: 2px of it is the floor people stand on, the rest hangs below. */
  readonly slabHeight: number;
  readonly shaft: number;
  readonly shaftEdge: number;
  readonly rail: number;
  paintShaft(g: Graphics, area: ShaftArea): void;
}

/** How wide the façade's right-hand edge is, in px. */
export const FACADE_EDGE = 9;

function line(
  g: Graphics,
  x1: number,
  y1: number,
  x2: number,
  y2: number,
  color: number,
  width = 1,
  alpha = 1,
): void {
  g.moveTo(x1, y1).lineTo(x2, y2).stroke({ color, width, alpha });
}

function mix(c1: number, c2: number, t: number): number {
  const channel = (shift: number): number =>
    Math.round(((c1 >> shift) & 255) + (((c2 >> shift) & 255) - ((c1 >> shift) & 255)) * t);
  return (channel(16) << 16) | (channel(8) << 8) | channel(0);
}

/** A vertical gradient as stacked bands: Pixi Graphics fills are flat. */
function vgrad(
  g: Graphics,
  x: number,
  y: number,
  w: number,
  h: number,
  from: number,
  to: number,
  steps = 10,
  alpha: [number, number] = [1, 1],
): void {
  for (let i = 0; i < steps; i += 1) {
    const t = i / (steps - 1);
    g.rect(x, y + (h * i) / steps, w, h / steps + 0.5).fill({
      color: mix(from, to, t),
      alpha: alpha[0] + (alpha[1] - alpha[0]) * t,
    });
  }
}

/** A mark on the shaft wall at every floor, so the dark well still reads in storeys. */
function floorMarks(g: Graphics, s: ShaftArea, color: number, alpha: number): void {
  for (const y of s.slabs) line(g, s.left + 4, y, s.right - 4, y, color, 1, alpha);
}

const OFFICE = {
  wall: 0xebe9e4,
  joint: 0xd3cec4,
  midJoint: 0xdcd8cf,
  tie: 0xcfc9be,
  lamp: 0xfbfaf6,
  lampGlow: 0xffffff,
  facade: 0xbdb9b1,
  facadeJoint: 0xa9a59c,
  slab: 0xa8a49b,
  shaft: 0x2f3032,
  shaftEdge: 0x1f2124,
  rail: LIFT_PARTS.steelDark,
  mark: 0x6f6963,
} as const;

const HOTEL = {
  wall: 0xf0e2cc,
  stripe: 0xe8d4b6,
  dado: 0xcfa983,
  dadoRail: 0x9a7350,
  moulding: 0xdcc39c,
  brick: 0xb98a74,
  mortar: 0x9c6f5c,
  stone: 0xe6d8c3,
  slab: 0xdccab0,
  shaft: 0x2b2628,
  shaftEdge: 0x1a1617,
  gold: LIFT_PARTS.brass,
} as const;

const TRANSIT = {
  wall: 0xf3f1ea,
  tileLine: 0xdedad0,
  band: 0x9fb9ae,
  bandLight: 0xc5d4cd,
  facade: 0xe2ddcf,
  facadeRivet: 0xa9a392,
  slab: 0x3f5248,
  shaft: 0x3e5248,
  shaftEdge: 0x24302a,
  rail: 0x8aa396,
  mark: 0x6f8a7c,
  shaftRivet: 0x9fb3a8,
} as const;

const SKYLINE = {
  wall: 0xeaf0f4,
  skyHigh: 0xcfe2ee,
  skyLow: 0xeef4f8,
  city: 0xd2dce4,
  mullion: 0xc3cfd8,
  glassHigh: 0xd9e7f1,
  glassLow: 0x9fb4c4,
  glint: 0xeef5fa,
  slab: 0x8d9aa8,
  shaft: 0x1d2540,
  shaftEdge: 0x141b2b,
  rail: 0x8fa3bf,
  mark: 0x8b9bb8,
  light: 0x7fd0e6,
} as const;

const officeTower: Atmosphere = {
  palette: OFFICE,
  wall: OFFICE.wall,
  paintWall(g, a) {
    // Poured concrete: panel joints, a seam at mid-height, form-tie holes.
    for (let x = a.x; x < a.right; x += 72) line(g, x, a.top, x, a.top + a.height, OFFICE.joint);
    const mid = a.top + a.height * 0.5;
    line(g, a.x, mid, a.right, mid, OFFICE.midJoint);
    for (let x = a.x + 18; x < a.right; x += 36) {
      for (let row = 1; row <= 3; row += 1) {
        g.circle(x, a.top + (a.height * row) / 4, 1.3).fill({ color: OFFICE.tie });
      }
    }
  },
  paintCeiling(g, a) {
    for (let x = a.x + 40; x < a.right - 30; x += 130) {
      g.rect(x, a.top + 1, 54, 3).fill({ color: OFFICE.lamp });
      vgrad(g, x - 10, a.top + 4, 74, 20, OFFICE.lampGlow, OFFICE.lampGlow, 6, [0.35, 0]);
    }
  },
  paintFacade(g, s) {
    g.rect(s.x, s.top, s.width, s.height).fill({ color: OFFICE.facade });
    for (let y = s.top + 6; y < s.top + s.height - 2; y += 12) {
      line(g, s.x + 2, y, s.x + s.width - 2, y, OFFICE.facadeJoint);
    }
  },
  slab: OFFICE.slab,
  slabHeight: 6,
  shaft: OFFICE.shaft,
  shaftEdge: OFFICE.shaftEdge,
  rail: OFFICE.rail,
  paintShaft(g, s) {
    floorMarks(g, s, OFFICE.mark, 0.7);
  },
};

const grandHotel: Atmosphere = {
  palette: HOTEL,
  wall: HOTEL.wall,
  paintWall(g, a) {
    // Striped paper above a wooden dado, a moulding along the ceiling.
    for (let x = a.x; x < a.right; x += 16) {
      g.rect(x, a.top, 8, a.height).fill({ color: HOTEL.stripe });
    }
    const dado = a.top + a.height * 0.68;
    g.rect(a.x, dado, a.right - a.x, a.top + a.height - dado).fill({
      color: HOTEL.dado,
      alpha: 0.55,
    });
    g.rect(a.x, dado - 2, a.right - a.x, 3).fill({ color: HOTEL.dadoRail });
    g.rect(a.x, a.top, a.right - a.x, 3).fill({ color: HOTEL.moulding });
  },
  paintFacade(g, s) {
    // Brick, with stone quoins down the building's outer edge.
    g.rect(s.x, s.top, s.width, s.height).fill({ color: HOTEL.brick });
    for (let y = s.top, row = 0; y < s.top + s.height; y += 6, row += 1) {
      line(g, s.x, y, s.x + s.width, y, HOTEL.mortar);
      for (let x = s.x + (row % 2) * 7; x < s.x + s.width; x += 14) {
        line(g, x, y, x, Math.min(y + 6, s.top + s.height), HOTEL.mortar);
      }
    }
    const qx = s.side === 'left' ? s.x : s.x + s.width - 9;
    for (let y = s.top + 4; y < s.top + s.height - 4; y += 18) {
      g.rect(qx, y, 9, 9).fill({ color: HOTEL.stone });
    }
  },
  slab: HOTEL.slab,
  slabHeight: 6,
  shaft: HOTEL.shaft,
  shaftEdge: HOTEL.shaftEdge,
  rail: HOTEL.gold,
  paintShaft(g, s) {
    floorMarks(g, s, HOTEL.gold, 0.5);
  },
};

const transitCentre: Atmosphere = {
  palette: TRANSIT,
  wall: TRANSIT.wall,
  paintWall(g, a) {
    // Metro tiles in running bond, a sage band at shoulder height.
    const tileW = 18;
    const tileH = 9;
    for (let y = a.top, row = 0; y < a.top + a.height; y += tileH, row += 1) {
      line(g, a.x, y, a.right, y, TRANSIT.tileLine);
      for (let x = a.x + (row % 2) * (tileW / 2); x < a.right; x += tileW) {
        line(g, x, y, x, Math.min(y + tileH, a.top + a.height), TRANSIT.tileLine);
      }
    }
    const band = a.top + a.height * 0.38;
    g.rect(a.x, band, a.right - a.x, 6).fill({ color: TRANSIT.band });
    g.rect(a.x, band + 8, a.right - a.x, 2).fill({ color: TRANSIT.bandLight });
  },
  paintFacade(g, s) {
    // Pale stone, riveted: the iron is in the slabs and the shaft.
    g.rect(s.x, s.top, s.width, s.height).fill({ color: TRANSIT.facade });
    const xs = s.width > 20 ? [s.x + 4, s.x + s.width - 4] : [s.x + s.width / 2];
    for (let y = s.top + 5; y < s.top + s.height; y += 10) {
      for (const x of xs) g.circle(x, y, 1.2).fill({ color: TRANSIT.facadeRivet });
    }
  },
  slab: TRANSIT.slab,
  slabHeight: 5,
  shaft: TRANSIT.shaft,
  shaftEdge: TRANSIT.shaftEdge,
  rail: TRANSIT.rail,
  paintShaft(g, s) {
    floorMarks(g, s, TRANSIT.mark, 0.7);
    for (let y = s.top + 6; y < s.top + s.height; y += 12) {
      g.circle(s.left + 5, y, 1.1).fill({ color: TRANSIT.shaftRivet });
      g.circle(s.right - 5, y, 1.1).fill({ color: TRANSIT.shaftRivet });
    }
  },
};

const skylineTower: Atmosphere = {
  palette: SKYLINE,
  wall: SKYLINE.wall,
  paintWall(g, a) {
    // Floor-to-ceiling glass: sky above the transom, a pale city below it.
    const transom = a.top + a.height * 0.62;
    vgrad(g, a.x, a.top, a.right - a.x, transom - a.top, SKYLINE.skyHigh, SKYLINE.skyLow);
    for (let x = a.x + 8, i = 0; x < a.right; x += 22, i += 1) {
      const h = 6 + ((i * 37) % 17);
      g.rect(x, transom - h, 16, h).fill({ color: SKYLINE.city });
    }
    for (let x = a.x; x < a.right; x += 46) {
      line(g, x, a.top, x, a.top + a.height, SKYLINE.mullion, 2);
    }
    line(g, a.x, transom, a.right, transom, SKYLINE.mullion, 2);
  },
  paintFacade(g, s) {
    vgrad(g, s.x, s.top, s.width, s.height, SKYLINE.glassHigh, SKYLINE.glassLow, 8);
    if (s.width > 20) {
      const mid = s.x + s.width / 2;
      line(g, mid, s.top, mid, s.top + s.height, SKYLINE.glint, 1, 0.8);
    }
  },
  slab: SKYLINE.slab,
  slabHeight: 4,
  shaft: SKYLINE.shaft,
  shaftEdge: SKYLINE.shaftEdge,
  rail: SKYLINE.rail,
  paintShaft(g, s) {
    floorMarks(g, s, SKYLINE.mark, 0.35);
    line(g, s.left + 12, s.top, s.left + 12, s.top + s.height, SKYLINE.light, 1, 0.35);
    line(g, s.right - 12, s.top, s.right - 12, s.top + s.height, SKYLINE.light, 1, 0.35);
  },
};

export const ATMOSPHERES: Readonly<Record<string, Atmosphere>> = {
  'office-tower': officeTower,
  'grand-hotel': grandHotel,
  'transit-centre': transitCentre,
  'skyline-tower': skylineTower,
};

/** A level with no building is in the Office Tower, as on the home screen. */
export function atmosphereOf(buildingId: string | undefined): Atmosphere {
  return ATMOSPHERES[buildingId ?? 'office-tower'] ?? officeTower;
}

/**
 * The ground a floor below the street is set in (ADR-0028): the Transit
 * Centre's metro is under the pavement, so its façade there is earth — topsoil
 * and roots just under the street, clay and stones further down. The same for
 * any building, since the ground is not a building's material.
 */
export const EARTH = {
  topsoil: 0x8f7a62,
  soil: 0xa08c74,
  deep: 0x8b7966,
  clay: 0xb8a284,
  stone: 0xcfc3b0,
  stoneEdge: 0xa89a86,
  pebble: 0xd9cfbe,
  root: 0x6b5a4a,
  pavement: 0xddd0b8,
  kerb: 0xb9a888,
} as const;

/** A small deterministic scatter, so the stones stay put from frame to frame. */
function scatter(seed: number, n: number): number[] {
  const out: number[] = [];
  let s = seed * 9301 + 49297;
  for (let i = 0; i < n; i += 1) {
    s = (s * 9301 + 49297) % 233280;
    out.push(s / 233280);
  }
  return out;
}

/** A strip of façade below the street: earth, `depth` storeys down (1 is just under the pavement). */
export function paintEarth(g: Graphics, s: FacadeStrip, depth: number): void {
  g.rect(s.x, s.top, s.width, s.height).fill({ color: depth <= 1 ? EARTH.soil : EARTH.deep });
  // A band of clay across each storey, at a slant that does not line up with the slabs.
  const clay = s.top + s.height * (depth % 2 === 0 ? 0.3 : 0.62);
  g.rect(s.x, clay, s.width, Math.max(3, s.height * 0.08)).fill({ color: EARTH.clay, alpha: 0.7 });
  const r = scatter(depth * 7 + (s.side === 'left' ? 1 : 3), 12);
  for (let i = 0; i + 2 < r.length; i += 3) {
    const x = s.x + 2 + (r[i] as number) * Math.max(1, s.width - 4);
    const y = s.top + 4 + (r[i + 1] as number) * (s.height - 8);
    const size = 1.2 + (r[i + 2] as number) * (s.width > 20 ? 2.4 : 1.4);
    g.circle(x, y, size).fill({ color: i % 2 === 0 ? EARTH.stone : EARTH.pebble });
    g.circle(x, y, size).stroke({ width: 0.6, color: EARTH.stoneEdge });
  }
  if (depth <= 1 && s.width > 20) {
    // Roots from the planting along the pavement, reaching down into the first storey.
    const x = s.x + s.width * 0.55;
    line(g, x, s.top, x - 3, s.top + s.height * 0.3, EARTH.root, 1.2);
    line(g, x - 3, s.top + s.height * 0.3, x - 7, s.top + s.height * 0.45, EARTH.root, 0.9);
    line(g, x - 3, s.top + s.height * 0.3, x + 2, s.top + s.height * 0.5, EARTH.root, 0.8);
  }
}

/** The street crossing the building's outside, at the top of its first floor underground. */
export function paintPavement(g: Graphics, x: number, y: number, width: number): void {
  g.rect(x, y - 3, width, 3).fill({ color: EARTH.pavement });
  g.rect(x, y - 4, width, 1.2).fill({ color: EARTH.kerb });
  g.rect(x, y, width, 3).fill({ color: EARTH.topsoil });
}
